<template>
    <v-dialog v-model="dialogValue" max-width="640" :fullscreen="isMobile">
        <DialogLayout title="Contact" subtitle="Send a note to the Mix n' Batch team" card-class="landing-dialog-card" @close="close">
            <v-alert v-if="hasSubmitted" type="success" variant="outlined"> Thanks! Your message has been sent. </v-alert>

            <template v-else>
                <v-form class="pt-2" @submit.prevent="submit">
                    <v-row>
                        <v-col cols="12">
                            <v-text-field
                                ref="emailInput"
                                v-model="email"
                                :disabled="submitting"
                                :error-messages="emailError ? [emailError] : []"
                                autocomplete="email"
                                density="compact"
                                label="Email"
                                placeholder="you@bakery.com"
                                required
                                type="email"
                                variant="outlined" />
                        </v-col>
                        <v-col cols="12">
                            <v-textarea
                                v-model="message"
                                :disabled="submitting"
                                :error-messages="messageError ? [messageError] : []"
                                auto-grow
                                density="compact"
                                label="Message"
                                placeholder="How can we help?"
                                required
                                rows="4"
                                variant="outlined" />
                        </v-col>
                    </v-row>
                </v-form>
            </template>

            <template #actions>
                <v-spacer />
                <v-btn variant="text" :disabled="submitting" @click="close">
                    {{ hasSubmitted ? 'Close' : 'Cancel' }}
                </v-btn>
                <v-btn v-if="!hasSubmitted" color="primary" variant="text" :disabled="!canSubmit" :loading="submitting" @click="submit"> Send </v-btn>
            </template>
        </DialogLayout>
    </v-dialog>
</template>

<script lang="ts" setup>
    import DialogLayout from '@/components/global/DialogLayout.vue';
    import { useNotifications } from '@/composables/useNotifications';
    import { useLandingIsMobile } from '@/pages/Landing/composables/useLandingIsMobile';
    import { validateEmail } from '@/pages/Landing/utils/landingValidators';
    import { landingPageInquiryService } from '@/services/LandingPageInquiryService';
    import { computed, nextTick, ref, watch } from 'vue';

    const props = defineProps<{
        modelValue: boolean;
    }>();

    const emit = defineEmits<{
        (e: 'update:modelValue', value: boolean): void;
    }>();

    const dialogValue = computed({
        get: () => props.modelValue,
        set: (value: boolean) => emit('update:modelValue', value),
    });

    const close = () => {
        dialogValue.value = false;
    };

    const { isMobile } = useLandingIsMobile();

    const { success: notifySuccess, error: notifyError } = useNotifications();

    const emailInput = ref<InstanceType<(typeof import('vuetify/components'))['VTextField']> | null>(null);
    const email = ref('');
    const message = ref('');
    const submitting = ref(false);
    const hasSubmitted = ref(false);

    const emailError = ref<string | null>(null);
    const messageError = ref<string | null>(null);

    const validateMessage = (value: string): string | null => {
        const trimmed = value.trim();
        if (!trimmed) {
            return 'Message is required.';
        }

        if (trimmed.length > 5000) {
            return 'Message is too long.';
        }

        return null;
    };

    const canSubmit = computed(() => {
        return !submitting.value && validateEmail(email.value) === null && validateMessage(message.value) === null;
    });

    const resetState = () => {
        email.value = '';
        message.value = '';
        submitting.value = false;
        hasSubmitted.value = false;
        emailError.value = null;
        messageError.value = null;
    };

    const submit = async () => {
        emailError.value = validateEmail(email.value);
        messageError.value = validateMessage(message.value);

        if (emailError.value || messageError.value) {
            return;
        }

        submitting.value = true;

        try {
            await landingPageInquiryService.submitInquiry(email.value.trim(), message.value.trim());
            hasSubmitted.value = true;
            notifySuccess('Message sent!');
        } catch (err: any) {
            notifyError(err?.response?.data?.message ?? 'Unable to send your message. Please try again.');
        } finally {
            submitting.value = false;
        }
    };

    watch(dialogValue, (open) => {
        if (open) {
            resetState();
            nextTick(() => {
                emailInput.value?.focus();
            });
        }
    });
</script>

<style lang="scss" scoped></style>
